import { Table } from "@tanstack/react-table"
import {
    ChevronLeft,
    ChevronRight,
    ChevronsLeft,
    ChevronsRight,
    // MoreHorizontal,  
} from "lucide-react"

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuTrigger,
} from "@/Components/ui/dropdown-menu"

import { Button } from "@/Components/ui/button"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/Components/ui/select"
import { GroupAction } from "./groupAction"
import React, { useContext } from "react"
import { WizardContext } from "../wizard/wizardProvider"
import { useIsMobile } from "@/hooks/use-mobile"

interface DataTablePaginationProps<TData> {
    table: Table<TData>,
    groupActions?: GroupAction[];
    viewOnly: boolean;
}

export function DataTablePagination<TData>({
    table,
    groupActions,
    viewOnly = false,
}: DataTablePaginationProps<TData>) {

    const wizard = useContext(WizardContext);
    const [open, setOpen] = React.useState(false);
    const isMobile = useIsMobile();

    React.useEffect(() => {
        if (wizard.showWizardPointer && wizard.currentStep?.sendClick && wizard.currentStep.stepComponentId === "table-group-actions") {
            // eslint-disable-next-line prefer-const
            let timeoutId = setTimeout(() => setOpen(true), 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [wizard.showWizardPointer, wizard.currentStep]);

    if (isMobile) {
        return (
            <div className="flex items-center justify-between px-2">
            {!viewOnly && (
                <div className="flex flex-col items-center text-xl text-muted-foreground">
                    <div>
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} selected
                    </div>
                    
                    {table.getFilteredSelectedRowModel().rows.length > 0 && groupActions && (
                        <div className="flex items-center space-x-2">
                            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="ml-2 p-2 text-xl" id="table-group-actions">
                                        <span className="sr-only">Open menu</span>
                                        {/* <MoreHorizontal className="h-4 w-4" />   */}
                                        Group Actions
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuLabel>Group Actions</DropdownMenuLabel>
                                    {groupActions && groupActions?.map((groupAction) => (
                                        <DropdownMenuItem
                                            key={groupAction.name}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const selectedRowIDs = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
                                                groupAction.action(selectedRowIDs);
                                                table.resetRowSelection();
                                            }}
                                        >
                                            {groupAction.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>)}
            <div className="flex items-center space-x-6 lg:space-x-8">
                <div className="flex items-center space-x-2">
                    <p className="text-xl font-medium">Rows per page</p>
                    <Select
                        value={`${table.getState().pagination.pageSize}`}
                        onValueChange={(value) => {
                            table.setPageSize(Number(value))
                        }}
                    >
                        <SelectTrigger className="h-16 w-[70px] text-xl" id="rowsPerPage">
                            <SelectValue placeholder={table.getState().pagination.pageSize} />
                        </SelectTrigger>
                        <SelectContent side="top">
                            {[5, 10, 20, 30, 40, 50, 100].map((pageSize) => (
                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                    {pageSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex w-[120px] items-center justify-center text-lg font-medium" id="pageIndicator">
                    Page {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount()}
                </div>
                <div className="flex items-center space-x-2" id="pagination-controls">
                    <Button
                        variant="outline"
                        className="hidden h-16 w-16 p-0"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">Go to first page</span>
                        <ChevronsLeft />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-16 w-16 p-0"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">Go to previous page</span>
                        <ChevronLeft />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-16 w-16 p-0"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">Go to next page</span>
                        <ChevronRight />
                    </Button>
                    <Button
                        variant="outline"
                        className="hidden h-16 w-16 p-0"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">Go to last page</span>
                        <ChevronsRight />
                    </Button>
                </div>
            </div>
        </div>
        );
    }

    return (
        <div className="flex items-center justify-between px-2">
            {!viewOnly && (
                <div className="flex flex-row items-center text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected

                    {table.getFilteredSelectedRowModel().rows.length > 0 && groupActions && (
                        <div className="flex items-center space-x-2">
                            <DropdownMenu open={open} onOpenChange={setOpen} modal={true}>
                                <DropdownMenuTrigger asChild>
                                    <Button variant="outline" className="ml-2 p-2" id="table-group-actions">
                                        <span className="sr-only">Open menu</span>
                                        {/* <MoreHorizontal className="h-4 w-4" />   */}
                                        Group Actions
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="start">
                                    <DropdownMenuLabel>Group Actions</DropdownMenuLabel>
                                    {groupActions && groupActions?.map((groupAction) => (
                                        <DropdownMenuItem
                                            key={groupAction.name}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const selectedRowIDs = table.getFilteredSelectedRowModel().rows.map((row) => row.original);
                                                groupAction.action(selectedRowIDs);
                                                table.resetRowSelection();
                                            }}
                                        >
                                            {groupAction.name}
                                        </DropdownMenuItem>
                                    ))}
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    )}
                </div>)}
            <div className="flex items-center space-x-6 lg:space-x-8">
                <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium">Rows per page</p>
                    <Select
                        value={`${table.getState().pagination.pageSize}`}
                        onValueChange={(value) => {
                            table.setPageSize(Number(value))
                        }}
                    >
                        <SelectTrigger className="h-8 w-[70px]" id="rowsPerPage">
                            <SelectValue placeholder={table.getState().pagination.pageSize} />
                        </SelectTrigger>
                        <SelectContent side="top">
                            {[5, 10, 20, 30, 40, 50, 100].map((pageSize) => (
                                <SelectItem key={pageSize} value={`${pageSize}`}>
                                    {pageSize}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="flex w-[100px] items-center justify-center text-sm font-medium" id="pageIndicator">
                    Page {table.getState().pagination.pageIndex + 1} of{" "}
                    {table.getPageCount()}
                </div>
                <div className="flex items-center space-x-2" id="pagination-controls">
                    <Button
                        variant="outline"
                        className="hidden h-8 w-8 p-0 lg:flex"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">Go to first page</span>
                        <ChevronsLeft />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        <span className="sr-only">Go to previous page</span>
                        <ChevronLeft />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-8 w-8 p-0"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">Go to next page</span>
                        <ChevronRight />
                    </Button>
                    <Button
                        variant="outline"
                        className="hidden h-8 w-8 p-0 lg:flex"
                        onClick={() => table.setPageIndex(table.getPageCount() - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        <span className="sr-only">Go to last page</span>
                        <ChevronsRight />
                    </Button>
                </div>
            </div>
        </div>
    )
}
